import { useState, useEffect } from "react";
import { copyText } from "@/lib/clipboard";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  Plus, Layout, Pencil, Trash2, Eye,
  BarChart3, Copy, Check, BadgeCheck, Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MICROSITE_THEMES as themes, MICROSITE_TEMPLATES as templates } from "@/components/MicrositeRenderer";

const formatNumber = (value) => Number(value || 0).toLocaleString("id-ID");

// ============ MAIN PAGE ============
const MicrositesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [microsites, setMicrosites] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Dialog "buat baru" — sengaja minimal (judul saja). Semua desain, tautan,
  // dan pengaturan lain diatur setelahnya di Microsite Studio (halaman penuh),
  // bukan di dialog kecil ini.
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => { fetchMicrosites(); }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (newSlug) checkSlugAvailability(newSlug);
      else setSlugStatus(null);
    }, 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSlug]);

  const fetchMicrosites = async () => {
    try {
      const res = await axios.get(`${API}/microsites`, { withCredentials: true });
      setMicrosites(res.data);
    } catch {
      toast.error("Gagal memuat microsites");
    } finally {
      setIsLoading(false);
    }
  };

  const checkSlugAvailability = async (slug) => {
    if (slug.length < 3) { setSlugStatus("too_short"); return; }
    if (!/^[a-z0-9\-]+$/.test(slug)) { setSlugStatus("invalid"); return; }
    setSlugStatus("checking");
    try {
      const res = await axios.get(`${API}/microsites/check-slug/${slug}`);
      setSlugStatus(res.data.available ? "available" : "taken");
    } catch {
      setSlugStatus("taken");
    }
  };

  const openCreateDialog = () => {
    setNewTitle("");
    setNewSlug("");
    setSlugStatus(null);
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) { toast.error("Judul harus diisi"); return; }
    setIsCreating(true);
    try {
      const res = await axios.post(
        `${API}/microsites`,
        { title: newTitle.trim(), custom_slug: newSlug || null },
        { withCredentials: true }
      );
      setCreateOpen(false);
      toast.success("Microsite dibuat — lanjutkan mendesainnya di Studio");
      navigate(`/microsites/${res.data.microsite_id}/studio`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat microsite");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Yakin ingin menghapus microsite ini?")) return;
    try {
      await axios.delete(`${API}/microsites/${id}`, { withCredentials: true });
      setMicrosites(microsites.filter(m => m.microsite_id !== id));
      toast.success("Microsite berhasil dihapus!");
    } catch { toast.error("Gagal menghapus microsite"); }
  };

  const getThemeColor = (t) => themes.find(x => x.value === t)?.color || "#7c3aed";

  const copyMicrositeUrl = async (ms) => {
    const url = `${window.location.origin}/m/${ms.slug}`;
    const ok = await copyText(url);
    if (ok) {
      setCopiedId(ms.microsite_id);
      toast.success("URL microsite disalin!");
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      toast.error("Gagal menyalin URL");
    }
  };

  // ======= RENDER =======
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="microsites-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Microsites
            </h1>
            <p className="text-muted-foreground mt-1">Buat halaman bio link yang menarik</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="create-microsite-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Microsite
          </Button>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : microsites.length === 0 ? (
          <Card className="dashboard-card">
            <CardContent className="py-12 text-center">
              <Layout className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">Belum ada microsite</h3>
              <p className="text-sm text-muted-foreground mb-4">Buat microsite pertama Anda</p>
              <Button onClick={openCreateDialog}><Plus className="w-4 h-4 mr-2" />Create Microsite</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {microsites.map((ms) => (
              <Card
                key={ms.microsite_id}
                className="dashboard-card overflow-hidden group"
                data-testid={`microsite-${ms.microsite_id}`}
              >
                {/* Cover preview */}
                <div className="h-24 relative"
                  style={{
                    background: ms.cover_url
                      ? `url(${ms.cover_url}) center/cover`
                      : `linear-gradient(135deg, ${getThemeColor(ms.theme)}55, ${getThemeColor(ms.theme)}18)`
                  }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-card/40 to-transparent" />
                  <div className="absolute bottom-0 left-4 translate-y-1/2">
                    {ms.avatar_url ? (
                      <img src={ms.avatar_url} alt={ms.title}
                        className="w-14 h-14 rounded-full border-[3px] border-card object-cover shadow-md" />
                    ) : (
                      <div className="w-14 h-14 rounded-full border-[3px] border-card flex items-center justify-center text-white font-bold text-lg shadow-md"
                        style={{ backgroundColor: getThemeColor(ms.theme) }}>
                        {ms.title.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="absolute top-2.5 right-2.5 flex gap-1.5">
                    <span className="text-[11px] font-medium bg-black/45 text-white px-2 py-0.5 rounded-full capitalize backdrop-blur-sm">
                      {templates.find(t => t.value === ms.template)?.label || "Classic"}
                    </span>
                  </div>
                </div>

                <CardContent className="pt-9 pb-4">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <h3 className="font-semibold truncate">{ms.title}</h3>
                    {ms.verified && (
                      <BadgeCheck className="w-4 h-4 text-primary shrink-0" fill="currentColor" stroke="hsl(var(--card))" />
                    )}
                  </div>
                  {ms.bio && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{ms.bio}</p>}

                  <button
                    onClick={() => copyMicrositeUrl(ms)}
                    className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors mt-2 group/copy"
                    title="Salin URL"
                    data-testid={`copy-${ms.microsite_id}`}
                  >
                    <span className="font-mono truncate">/m/{ms.slug}</span>
                    {copiedId === ms.microsite_id ? (
                      <Check className="w-3 h-3 text-green-500 shrink-0" />
                    ) : (
                      <Copy className="w-3 h-3 shrink-0 opacity-60 group-hover/copy:opacity-100" />
                    )}
                  </button>

                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {formatNumber(ms.views)}</span>
                    <span className="flex items-center gap-1"><Layout className="w-3 h-3" /> {ms.links?.length || 0} link</span>
                  </div>

                  <div className="flex items-center gap-2 mt-3.5">
                    <Button variant="outline" size="sm" className="flex-1"
                      onClick={() => window.open(`/m/${ms.slug}`, "_blank")}
                      data-testid={`view-${ms.microsite_id}`}>
                      <Eye className="w-4 h-4 mr-2" /> Preview
                    </Button>

                    <Button variant="ghost" size="icon" className="h-9 w-9"
                      onClick={() => navigate(`/microsites/${ms.microsite_id}/analytics`)}
                      data-testid={`analytics-${ms.microsite_id}`}
                      title="Analytics">
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9"
                      onClick={() => navigate(`/microsites/${ms.microsite_id}/studio`)}
                      data-testid={`edit-${ms.microsite_id}`}
                      title="Edit di Studio">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive"
                      onClick={() => handleDelete(ms.microsite_id)} data-testid={`delete-${ms.microsite_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog buat baru — minimal, lanjut ke Studio */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Microsite Baru</DialogTitle>
              <DialogDescription>
                Beri nama dulu, semua desain dan tautan diatur berikutnya di Studio.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Judul *</Label>
                <Input
                  placeholder="Profil Saya"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  autoFocus
                  data-testid="microsite-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Custom URL <span className="text-xs text-muted-foreground ml-1">(opsional)</span>
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {window.location.origin}/m/
                  </span>
                  <div className="relative flex-1">
                    <Input
                      placeholder="nama-kamu"
                      value={newSlug}
                      onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, ""))}
                    />
                    {slugStatus === "checking" && (
                      <span className="absolute right-2 top-2 text-xs text-muted-foreground">memeriksa...</span>
                    )}
                    {(slugStatus === "taken" || slugStatus === "invalid" || slugStatus === "too_short") && (
                      <span className="absolute right-2 top-2 text-red-500">✕</span>
                    )}
                  </div>
                </div>
                {slugStatus === "available" && <p className="text-xs text-green-500">✓ URL tersedia</p>}
                {slugStatus === "taken" && <p className="text-xs text-red-500">URL sudah digunakan</p>}
                {slugStatus === "invalid" && <p className="text-xs text-red-500">Hanya huruf kecil, angka, dan tanda (-)</p>}
                {slugStatus === "too_short" && <p className="text-xs text-red-500">Minimal 3 karakter</p>}
                {!newSlug && <p className="text-xs text-muted-foreground">Kosongkan untuk generate otomatis</p>}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating || slugStatus === "checking" || slugStatus === "taken" || slugStatus === "invalid"}
                  data-testid="microsite-submit-btn"
                >
                  {isCreating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Membuat...</> : "Lanjut ke Studio →"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MicrositesPage;
